import { Plus, X } from 'lucide-react';

export const EMPTY_FIELD = { key: '', options: [] };

export function cleanFields(fields) {
  return (fields || [])
    .map(f => ({
      key: (f.key || '').trim(),
      options: (f.options || []).map(o => o.trim()).filter(Boolean),
    }))
    .filter(f => f.key);
}

function OptionsList({ options, onChange, write, placeholder }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
      {options.map((opt, idx) => (
        <div key={idx} className="flex items-center gap-1 bg-gray-100 rounded-md pl-2 pr-1 py-1">
          <input
            type="text"
            value={opt}
            disabled={!write}
            onChange={e => {
              const updated = [...options];
              updated[idx] = e.target.value;
              onChange(updated);
            }}
            placeholder={placeholder || 'Option'}
            className="w-20 bg-transparent text-xs focus:outline-none disabled:cursor-default"
          />
          {write && (
            <button type="button" onClick={() => onChange(options.filter((_, i) => i !== idx))}
              className="text-gray-400 hover:text-red-600">
              <X size={10} />
            </button>
          )}
        </div>
      ))}
      {write && (
        <button type="button" onClick={() => onChange([...options, ''])}
          className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-0.5">
          <Plus size={10} /> Add option
        </button>
      )}
      {!write && options.length === 0 && <span className="text-xs text-gray-300">Free text</span>}
    </div>
  );
}

export default function FieldDefinitionEditor({ fields, onChange, write }) {
  const updateField = (idx, patch) => {
    const updated = [...fields];
    updated[idx] = { ...updated[idx], ...patch };
    onChange(updated);
  };
  const removeField = (idx) => onChange(fields.filter((_, i) => i !== idx));
  const addField = () => onChange([...fields, { ...EMPTY_FIELD }]);

  return (
    <div className="space-y-3">
      {fields.map((field, idx) => (
        <div key={idx} className="border border-gray-200 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={field.key}
              disabled={!write}
              onChange={e => updateField(idx, { key: e.target.value })}
              placeholder="e.g. Color"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {write && (
              <button type="button" onClick={() => removeField(idx)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                <X size={14} />
              </button>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Predefined values (e.g. Red, Blue) — leave empty to type them freely at stock-in/ordering time
          </p>
          <OptionsList options={field.options} write={write} onChange={opts => updateField(idx, { options: opts })} />
        </div>
      ))}
      {fields.length === 0 && (
        <p className="text-sm text-gray-400 py-2">No fields defined yet.</p>
      )}
      {write && (
        <button type="button" onClick={addField}
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium">
          <Plus size={14} /> Add field
        </button>
      )}
    </div>
  );
}
